import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  useToast,
  Spinner,
  Tabs,
  TabList,
  Tab,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  AddIcon,
} from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";

const PAGE_SIZE = 10;

const AdminSupport = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({ title: "", content: "" });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const getTableName = () => {
    if (activeTab === 0) return "faq";
    if (activeTab === 1) return "notice";
    if (activeTab === 2) return "revenue_notice";
    return "faq";
  };

  const getTabTitle = () => {
    if (activeTab === 0) return "FAQ";
    if (activeTab === 1) return "공지사항";
    if (activeTab === 2) return "수익 공지";
    return "FAQ";
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const tableName = getTableName();

    try {
      let query = supabase.from(tableName).select("*", { count: "exact" });

      // 검색 조건
      if (searchQuery) {
        query = query.or(
          `title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`
        );
      }

      const {
        data: result,
        error,
        count,
      } = await query
        .order("created_date", { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (error) {
        throw error;
      }

      setData(result || []);
      setTotalCount(count || 0);
      setTotalPages(Math.ceil((count || 0) / PAGE_SIZE));
    } catch (error) {
      console.error("데이터 조회 실패:", error);
      toast({
        title: "데이터 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setData([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, activeTab, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchTrigger = () => {
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchTrigger();
    }
  };

  const handleNewItem = () => {
    setSelectedItem(null);
    setIsEditMode(false);
    setFormData({ title: "", content: "" });
    onOpen();
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEditMode(true);
    setFormData({ title: item.title || "", content: item.content || "" });
    onOpen();
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: "입력 오류",
        description: "제목과 내용을 모두 입력해주세요.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const tableName = getTableName();
      let result;

      if (isEditMode) {
        // 수정
        const { error } = await supabase
          .from(tableName)
          .update({
            title: formData.title,
            content: formData.content,
          })
          .eq("id", selectedItem.id);

        if (error) throw error;
      } else {
        // 등록
        const { error } = await supabase.from(tableName).insert({
          title: formData.title,
          content: formData.content,
        });

        if (error) throw error;
      }

      toast({
        title: isEditMode ? "수정 완료" : "등록 완료",
        description: `${getTabTitle()}가 ${
          isEditMode ? "수정" : "등록"
        }되었습니다.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onClose();
      fetchData();
    } catch (error) {
      console.error("저장 실패:", error);
      toast({
        title: "저장 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}.`;
  };

  const columns = [
    { key: "title", label: "제목" },
    { key: "created_date", label: "등록일" },
  ];

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">
          FAQ / 공지사항 관리
        </Heading>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={handleNewItem}
        >
          {getTabTitle()} 등록
        </Button>
      </Flex>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        {/* 탭 */}
        <Tabs index={activeTab} onChange={setActiveTab} mb={4}>
          <TabList>
            <Tab>FAQ</Tab>
            <Tab>공지사항</Tab>
            <Tab>수익 공지</Tab>
          </TabList>
        </Tabs>

        {/* 검색 */}
        <Flex mb={4}>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={`${getTabTitle()} 검색`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
          <Button ml={2} onClick={handleSearchTrigger}>
            검색
          </Button>
        </Flex>

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner />
          </Flex>
        ) : (
          <>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  {columns.map((column) => (
                    <Th key={column.key}>{column.label}</Th>
                  ))}
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((item, index) => (
                  <Tr key={item.id || index}>
                    <Td>
                      <Text fontWeight="bold" noOfLines={1}>
                        {item.title || "-"}
                      </Text>
                    </Td>

                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(item.created_date)}
                      </Text>
                    </Td>
                    <Td>
                      <Button
                        size="xs"
                        leftIcon={<EditIcon />}
                        onClick={() => handleEdit(item)}
                      >
                        수정
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Flex justify="space-between" align="center" mt={6}>
              <Text fontSize="sm" color="gray.600">
                총 {totalCount}개 {getTabTitle()}
              </Text>
              <Flex align="center">
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                  size="sm"
                />
                <Text mx={4} fontSize="sm">
                  {page} / {totalPages}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  isDisabled={page === totalPages}
                  size="sm"
                />
              </Flex>
            </Flex>
          </>
        )}
      </Box>

      {/* 등록/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditMode ? `${getTabTitle()} 수정` : `${getTabTitle()} 등록`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={4}>
              <Text fontWeight="bold" mb={2}>
                제목
              </Text>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="제목을 입력하세요"
              />
            </Box>
            <Box>
              <Text fontWeight="bold" mb={2}>
                내용
              </Text>
              <Textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                placeholder="내용을 입력하세요"
                rows={20}
                resize="vertical"
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleSave}>
              {isEditMode ? "수정" : "등록"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminSupport;
